import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RefreshCw, Calendar, TrendingUp, History as HistoryIcon, Globe, AlertTriangle, ShieldAlert, LogOut, User, Wifi, WifiOff, Download, Newspaper, Clock, Loader2, Target, Columns, ChevronDown, Settings2, Plus, Trash2, Zap, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Only India is supported - other countries removed per user request
const COUNTRIES = [
  { id: "india", name: "India", flag: "🇮🇳", currency: "₹", exchange: "NSE" },
];

// Define all available columns for the predictions table
const ALL_COLUMNS = [
  { id: "stock", label: "Stock", default: true },
  { id: "startPrice", label: "Start Price", default: true },
  { id: "livePrice", label: "Live Price", default: false },
  { id: "predicted", label: "Predicted (2d)", default: true },
  { id: "actualPrice", label: "Actual Price", default: true },
  { id: "newsImpact", label: "News Impact", default: false },
  { id: "investing", label: "Investing", default: false },
  { id: "expectedDate", label: "Expected Date", default: true },
  { id: "strategy", label: "Strategy", default: true },
  { id: "summary", label: "Prediction Summary", default: false },
  { id: "grossProfit", label: "Gross Profit", default: false },
  { id: "commission", label: "Commission", default: false },
  { id: "netProfit", label: "Net Profit", default: false },
  { id: "actions", label: "Actions", default: true },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { isConnected, priceUpdates, predictionUpdates, subscribeToCountry, clearPriceUpdates } = useWebSocket();
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [livePrices, setLivePrices] = useState({});
  const [investmentAmount, setInvestmentAmount] = useState(1000000);
  const [commissionPct, setCommissionPct] = useState(0.2);  // Default 0.2%
  const [selectedCountry, setSelectedCountry] = useState("india");
  const [isSyncing, setIsSyncing] = useState(false);
  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false);
  
  // Custom stock management
  const [addStockDialogOpen, setAddStockDialogOpen] = useState(false);
  const [newStockSymbol, setNewStockSymbol] = useState("");
  const [addingStock, setAddingStock] = useState(false);
  const [customStocks, setCustomStocks] = useState([]);
  const [generatingPrediction, setGeneratingPrediction] = useState(null);
  
  // Manual evaluation state
  const [actualPriceInputs, setActualPriceInputs] = useState({});
  const [evaluatingStock, setEvaluatingStock] = useState(null);
  
  // Column visibility state - initialize from localStorage or defaults
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('dashboardVisibleColumns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return ALL_COLUMNS.filter(c => c.default).map(c => c.id);
      }
    }
    return ALL_COLUMNS.filter(c => c.default).map(c => c.id);
  });

  const currentCountry = COUNTRIES.find(c => c.id === selectedCountry) || COUNTRIES[0];

  // Save column visibility to localStorage
  useEffect(() => {
    localStorage.setItem('dashboardVisibleColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Handle column visibility toggle
  const handleColumnToggle = (columnId) => {
    if (visibleColumns.includes(columnId)) {
      // Don't allow hiding all columns - keep at least one
      if (visibleColumns.length > 1) {
        setVisibleColumns(visibleColumns.filter(id => id !== columnId));
      }
    } else {
      setVisibleColumns([...visibleColumns, columnId]);
    }
  };

  // Handle select all columns
  const handleSelectAllColumns = () => {
    if (visibleColumns.length === ALL_COLUMNS.length) {
      // Keep only default columns
      setVisibleColumns(ALL_COLUMNS.filter(c => c.default).map(c => c.id));
    } else {
      setVisibleColumns(ALL_COLUMNS.map(c => c.id));
    }
  };

  // Reset columns to default
  const handleResetColumns = () => {
    setVisibleColumns(ALL_COLUMNS.filter(c => c.default).map(c => c.id));
    toast.success("Columns reset to default");
  };

  // Check if column is visible
  const isColumnVisible = (columnId) => visibleColumns.includes(columnId);

  // Admin: Sync production data
  const handleAdminSync = async () => {
    if (!window.confirm("This will reset all evaluations and update prediction prices. Continue?")) {
      return;
    }
    try {
      setIsSyncing(true);
      toast.info("Syncing data...", { duration: 10000 });
      const res = await axios.post(`${API}/admin/full-data-sync`);
      toast.success(`Sync complete! Reset: ${res.data.reset_count}, Updated: ${res.data.updated_count} predictions`);
      // Refresh predictions after sync
      fetchPredictions(selectedCountry);
    } catch (err) {
      console.error("Sync error:", err);
      toast.error(`Sync failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
  };

  // ==================== Custom Stock Management ====================
  
  // Fetch custom stocks
  const fetchCustomStocks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stocks/custom`);
      setCustomStocks(res.data.stocks || []);
    } catch (err) {
      console.error("Error fetching custom stocks:", err);
    }
  }, []);

  // Add a new custom stock - now auto-generates prediction immediately
  const handleAddStock = async () => {
    if (!newStockSymbol.trim()) {
      toast.error("Please enter a stock symbol");
      return;
    }
    
    try {
      setAddingStock(true);
      
      const res = await axios.post(`${API}/stocks/custom`, {
        symbol: newStockSymbol.trim()
      });
      
      // Show prediction details if available
      if (res.data.prediction) {
        const pred = res.data.prediction;
        toast.success(
          `${res.data.stock.name} added!\nDirection: ${pred.direction} | Target: ₹${pred.predicted_price.toFixed(2)}`,
          { duration: 5000 }
        );
      } else {
        toast.success(res.data.message);
      }
      
      setAddStockDialogOpen(false);
      setNewStockSymbol("");
      
      // Refresh predictions to show the new stock in the main grid
      fetchCustomStocks();
      fetchPredictions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add stock");
    } finally {
      setAddingStock(false);
    }
  };

  // Remove a custom stock
  const handleRemoveCustomStock = async (stockId, symbol) => {
    if (!window.confirm(`Remove ${symbol} from custom stocks?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/stocks/custom/${stockId}`);
      toast.success(`${symbol} removed from custom stocks`);
      fetchCustomStocks();
      fetchPredictions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove stock");
    }
  };

  // Generate prediction for a custom stock immediately
  const handleGenerateCustomPrediction = async (stockId, symbol) => {
    try {
      setGeneratingPrediction(stockId);
      toast.info(`Generating prediction for ${symbol}...`, { duration: 30000 });
      
      const res = await axios.post(`${API}/stocks/custom/${stockId}/generate-prediction`);
      toast.success(`Prediction generated for ${symbol}: ${res.data.prediction.direction}`);
      
      // Refresh data
      fetchCustomStocks();
      fetchPredictions();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to generate prediction for ${symbol}`);
    } finally {
      setGeneratingPrediction(null);
    }
  };

  // Load custom stocks on mount
  useEffect(() => {
    fetchCustomStocks();
  }, [fetchCustomStocks]);

  // ==================== Manual Stock Evaluation ====================
  
  // Handle actual price input change
  const handleActualPriceChange = (predictionId, value) => {
    setActualPriceInputs(prev => ({
      ...prev,
      [predictionId]: value
    }));
  };

  // Submit manual evaluation
  const handleManualEvaluate = async (prediction) => {
    const actualPriceStr = actualPriceInputs[prediction.prediction_id];
    const actualPrice = parseFloat(actualPriceStr);
    
    if (!actualPriceStr || isNaN(actualPrice) || actualPrice <= 0) {
      toast.error("Please enter a valid actual price");
      return;
    }
    
    try {
      setEvaluatingStock(prediction.prediction_id);
      
      const res = await axios.post(`${API}/predictions/evaluate-single`, {
        prediction_id: prediction.prediction_id,
        actual_price: actualPrice
      });
      
      const result = res.data.prediction;
      const profitSign = result.profit_pct >= 0 ? '+' : '';
      const displaySymbol = prediction.symbol.replace('.NS', '');
      const successText = result.is_success ? '✓ Success' : '✗ Failed';
      
      toast.success(
        `${displaySymbol} evaluated! Profit: ${profitSign}${result.profit_pct.toFixed(2)}% | ${successText}`,
        { duration: 5000 }
      );
      
      // Clear the input
      setActualPriceInputs(prev => {
        const newInputs = {...prev};
        delete newInputs[prediction.prediction_id];
        return newInputs;
      });
      
      // Refresh predictions - the evaluated stock will automatically move to History
      fetchPredictions();
    } catch (err) {
      const errorMsg = err.response?.data?.detail;
      // Handle FastAPI validation errors (array of {type, loc, msg, input, url})
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg).join(', ') || "Validation error");
      } else if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error("Failed to evaluate stock");
      }
    } finally {
      setEvaluatingStock(null);
    }
  };

  const fetchPredictions = async (country = selectedCountry) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/predictions/latest?country=${country}`);
      
      const latestByStock = {};
      (res.data.predictions || []).forEach(pred => {
        if (pred.horizon === 2) {
          if (!latestByStock[pred.symbol] || 
              new Date(pred.prediction_date) > new Date(latestByStock[pred.symbol].prediction_date)) {
            latestByStock[pred.symbol] = pred;
          }
        }
      });
      
      // Sort by "hotness" - biggest price movers first
      const sortedPredictions = Object.values(latestByStock).sort((a, b) => {
        const startA = a.start_price || a.current_price;
        const startB = b.start_price || b.current_price;
        const changeA = Math.abs((a.predicted_price - startA) / startA * 100);
        const changeB = Math.abs((b.predicted_price - startB) / startB * 100);
        return changeB - changeA; // Biggest movers first
      });
      
      setPredictions(sortedPredictions);
      
      if (sortedPredictions.length > 0) {
        toast.success(`${sortedPredictions.length} predictions loaded`);
      }
      
      // If fewer than 20 predictions, show warning and offer to regenerate
      if (sortedPredictions.length < 20 && sortedPredictions.length > 0) {
        toast.warning(`Only ${sortedPredictions.length}/20 stocks found. Click "Generate Predictions" to create all 20.`, {
          duration: 10000
        });
      } else if (sortedPredictions.length === 0) {
        toast.info("No predictions found. Click 'Generate Predictions' to create new predictions.", {
          duration: 10000
        });
      }
    } catch (err) {
      toast.error("Failed to load predictions");
    } finally {
      setLoading(false);
    }
  };

  const generatePredictions = async () => {
    try {
      setLoading(true);
      toast.info("Generating predictions for all 20 stocks... This may take 1-2 minutes.", { duration: 60000 });
      
      const response = await axios.post(`${API}/predictions/daily-snapshot?country=${selectedCountry}`, {}, {
        timeout: 180000 // 3 minute timeout
      });
      
      const count = response.data?.predictions_count || 0;
      toast.success(`Generated ${count} predictions successfully!`, { duration: 5000 });
      
      // Wait a bit then refresh
      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchPredictions();
    } catch (err) {
      console.error("Generate error:", err);
      toast.error(`Failed to generate: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Update live prices from WebSocket
  useEffect(() => {
    if (Object.keys(priceUpdates).length > 0) {
      setLivePrices(prev => ({ ...prev, ...priceUpdates }));
    }
  }, [priceUpdates]);

  // Handle prediction updates from WebSocket
  useEffect(() => {
    if (predictionUpdates.length > 0) {
      const latestUpdate = predictionUpdates[0];
      
      // Update predictions with new data
      setPredictions(prev => {
        return prev.map(pred => {
          if (pred.id === latestUpdate.prediction_id || pred.symbol === latestUpdate.symbol) {
            if (latestUpdate.action === "locked") {
              return { ...pred, locked: true, locked_reason: latestUpdate.reason };
            } else if (latestUpdate.action === "updated") {
              return {
                ...pred,
                predicted_price: latestUpdate.new_predicted_price,
                previous_predicted_price: latestUpdate.old_predicted_price,
                direction: latestUpdate.direction,
                analysis_score: latestUpdate.analysis_score || pred.analysis_score
              };
            }
          }
          return pred;
        });
      });
      
      // Show toast for significant updates
      if (latestUpdate.action === "locked") {
        toast.success(`${latestUpdate.symbol.replace(/\.(NS|KA|NP|CM|DU)$/, "")} target reached! 🎯`);
      }
    }
  }, [predictionUpdates]);

  useEffect(() => {
    fetchPredictions(selectedCountry);
  }, [selectedCountry]);

  // Subscribe to WebSocket for selected country
  useEffect(() => {
    if (selectedCountry) {
      subscribeToCountry(selectedCountry);
      clearPriceUpdates();
      setLivePrices({});
    }
  }, [selectedCountry, subscribeToCountry, clearPriceUpdates]);

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setPredictions([]);
    setLivePrices({});
  };

  // Format timestamp based on country timezone
  const formatTimestamp = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    
    // Timezone offsets from UTC (in hours)
    const timezoneOffsets = {
      "usa": -5,      // EST (Eastern Standard Time)
      "india": 5.5,   // IST (Indian Standard Time)
      "pakistan": 5,  // PKT (Pakistan Standard Time)
      "nepal": 5.75,  // NPT (Nepal Time) UTC+5:45
      "srilanka": 5.5,// LKT (Lanka Time) UTC+5:30
      "dubai": 4,     // GST (Gulf Standard Time)
    };
    
    const offset = timezoneOffsets[selectedCountry] || 5.5;
    const localDate = new Date(date.getTime() + (offset * 60 * 60 * 1000));
    const day = localDate.getUTCDate().toString().padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[localDate.getUTCMonth()];
    
    // Market open times per country
    const marketOpenTime = {
      "usa": "09:30",
      "india": "09:15",
      "pakistan": "09:30",
      "nepal": "11:00",
      "srilanka": "09:30",
      "dubai": "10:00",
    };
    
    const timezoneLabels = {
      "usa": "EST",
      "india": "IST",
      "pakistan": "PKT",
      "nepal": "NPT",
      "srilanka": "LKT",
      "dubai": "GST",
    };
    
    const openTime = marketOpenTime[selectedCountry] || "09:30";
    const tzLabel = timezoneLabels[selectedCountry] || "IST";
    return `${day} ${month}, ${openTime} ${tzLabel}`;
  };

  // USA holidays 2025-2026 (NYSE/NASDAQ)
  const USA_HOLIDAYS = new Set([
    "2025-01-01", "2025-01-20", "2025-02-17", "2025-04-18", "2025-05-26",
    "2025-06-19", "2025-07-04", "2025-09-01", "2025-11-27", "2025-12-25",
    "2026-01-01", "2026-01-19", "2026-02-16", "2026-04-03", "2026-05-25",
    "2026-06-19", "2026-07-03", "2026-09-07", "2026-11-26", "2026-12-25",
  ]);

  // India holidays 2025-2026 (NSE)
  const INDIA_HOLIDAYS = new Set([
    "2025-01-26", "2025-02-26", "2025-03-14", "2025-03-31", "2025-04-10",
    "2025-04-14", "2025-04-18", "2025-05-01", "2025-06-07", "2025-08-15",
    "2025-08-16", "2025-08-27", "2025-10-02", "2025-10-21", "2025-10-22",
    "2025-11-05", "2025-12-25",
    "2026-01-26", "2026-03-03", "2026-03-17", "2026-03-30", "2026-04-03",
    "2026-04-14", "2026-05-01", "2026-05-27", "2026-08-15", "2026-09-16",
    "2026-10-02", "2026-10-08", "2026-10-26", "2026-10-27", "2026-11-09",
    "2026-12-25",
  ]);

  // Pakistan holidays 2025-2026 (PSX)
  const PAKISTAN_HOLIDAYS = new Set([
    "2025-02-05", "2025-03-23", "2025-03-31", "2025-04-01", "2025-05-01",
    "2025-06-07", "2025-06-08", "2025-06-09", "2025-07-05", "2025-08-14",
    "2025-09-12", "2025-11-09", "2025-12-25",
    "2026-02-05", "2026-03-20", "2026-03-21", "2026-03-23", "2026-05-01",
    "2026-05-27", "2026-05-28", "2026-05-29", "2026-08-14", "2026-09-01",
    "2026-11-09", "2026-12-25",
  ]);

  // Nepal holidays 2025-2026 (NEPSE)
  const NEPAL_HOLIDAYS = new Set([
    "2025-01-15", "2025-01-30", "2025-02-19", "2025-03-08", "2025-03-14",
    "2025-04-14", "2025-05-01", "2025-05-29", "2025-08-19", "2025-09-15",
    "2025-10-20", "2025-10-21", "2025-10-22", "2025-10-23", "2025-10-24",
    "2025-11-01", "2025-11-02", "2025-11-03",
    "2026-01-15", "2026-01-30", "2026-02-19", "2026-03-04", "2026-03-08",
    "2026-04-14", "2026-05-01", "2026-05-29", "2026-08-08", "2026-09-15",
    "2026-10-09", "2026-10-10", "2026-10-11", "2026-10-12", "2026-10-13",
    "2026-10-21", "2026-10-22", "2026-10-23",
  ]);

  // Sri Lanka holidays 2025-2026 (CSE)
  const SRILANKA_HOLIDAYS = new Set([
    "2025-01-14", "2025-01-15", "2025-02-04", "2025-02-12", "2025-03-14",
    "2025-04-12", "2025-04-13", "2025-04-14", "2025-05-01", "2025-05-12",
    "2025-06-10", "2025-12-25",
    "2026-01-05", "2026-01-14", "2026-02-03", "2026-02-04", "2026-03-04",
    "2026-04-02", "2026-04-13", "2026-04-14", "2026-05-01", "2026-05-30",
    "2026-12-25",
  ]);

  // Dubai holidays 2025-2026 (DFM - Friday-Saturday weekend)
  const DUBAI_HOLIDAYS = new Set([
    "2025-01-01", "2025-03-31", "2025-04-01", "2025-04-02",
    "2025-06-05", "2025-06-06", "2025-06-07", "2025-07-07",
    "2025-09-15", "2025-12-01", "2025-12-02", "2025-12-03",
    "2026-01-01", "2026-03-20", "2026-03-21", "2026-03-22",
    "2026-05-26", "2026-05-27", "2026-05-28", "2026-06-17",
    "2026-08-26", "2026-12-01", "2026-12-02", "2026-12-03",
  ]);

  // Check if a date is a trading day
  const isTradingDay = (date, country) => {
    const day = date.getDay();
    const dateStr = date.toISOString().split('T')[0];
    
    // Dubai: Friday-Saturday weekend (Sunday-Thursday trading)
    if (country === "dubai") {
      if (day === 5 || day === 6) return false; // Friday=5, Saturday=6
      return !DUBAI_HOLIDAYS.has(dateStr);
    }
    
    // Other countries: Saturday-Sunday weekend
    if (day === 0 || day === 6) return false; // Sunday=0, Saturday=6
    
    // Use appropriate holiday set based on country
    const holidaySets = {
      "usa": USA_HOLIDAYS,
      "india": INDIA_HOLIDAYS,
      "pakistan": PAKISTAN_HOLIDAYS,
      "nepal": NEPAL_HOLIDAYS,
      "srilanka": SRILANKA_HOLIDAYS,
    };
    
    const holidays = holidaySets[country] || INDIA_HOLIDAYS;
    return !holidays.has(dateStr);
  };

  // Get evaluation date: 3 days including prediction date (so 2 trading days after)
  const getFutureDate = (predictionDate) => {
    const pred = new Date(predictionDate);
    let future = new Date(pred);
    let tradingDays = 0;
    
    // Count 2 trading days after prediction date
    // (prediction day counts as day 1, so we need day 2 and day 3)
    while (tradingDays < 2) {
      future.setDate(future.getDate() + 1);
      if (isTradingDay(future, selectedCountry)) {
        tradingDays++;
      }
    }
    
    // Use appropriate date format based on country
    if (selectedCountry === "usa" || selectedCountry === "pakistan" || selectedCountry === "nepal" || selectedCountry === "srilanka" || selectedCountry === "dubai") {
      return future.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
    }
    return future.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  // Expected number of stocks per country (base is 20 stocks, but use actual count for distribution)
  const STOCKS_PER_COUNTRY = 20;

  // Smart Investment Allocation with Whole Shares Only (India market restriction)
  // Priority: High-price stocks (price > allocation) get 1 share first, then normal allocation
  const calculateSmartAllocation = () => {
    if (!investmentAmount || predictions.length === 0) {
      return {
        allocations: [],
        totalInvested: 0,
        unusedCash: investmentAmount || 0,
        leftoverPool: 0,
        highPriceStockCount: 0
      };
    }

    const actualStockCount = predictions.length;
    const initialAllocationPerStock = investmentAmount / actualStockCount;
    
    // Prepare stock data with prices
    let stockData = predictions.map(pred => {
      const startPrice = pred.start_price || pred.current_price || 0;
      const predId = pred.id || pred.prediction_id;
      return {
        symbol: pred.symbol,
        id: predId,
        startPrice,
        direction: pred.direction,
        predicted_price: pred.predicted_price,
        isHighPrice: startPrice > initialAllocationPerStock && startPrice > 0
      };
    });
    
    // ==================== PHASE 1: HIGH-PRICE STOCKS FIRST ====================
    // Identify high-price stocks (price > allocation per stock)
    // These stocks MUST get at least 1 share allocated first
    const highPriceStocks = stockData.filter(s => s.isHighPrice);
    const normalPriceStocks = stockData.filter(s => !s.isHighPrice && s.startPrice > 0);
    const invalidStocks = stockData.filter(s => s.startPrice <= 0);
    
    let remainingInvestment = investmentAmount;
    let stockAllocations = [];
    
    // Allocate 1 share to each high-price stock first
    for (const stock of highPriceStocks) {
      const shares = 1;
      const investmentUsed = stock.startPrice;
      remainingInvestment -= investmentUsed;
      
      stockAllocations.push({
        symbol: stock.symbol,
        id: stock.id,
        startPrice: stock.startPrice,
        shares,
        investmentUsed,
        unusedAmount: 0, // High-price stocks don't contribute to leftover initially
        direction: stock.direction,
        predicted_price: stock.predicted_price,
        isHighPrice: true
      });
    }
    
    // ==================== PHASE 2: NORMAL ALLOCATION ====================
    // Calculate new allocation per stock for remaining normal stocks
    const normalStockCount = normalPriceStocks.length;
    const newAllocationPerStock = normalStockCount > 0 
      ? remainingInvestment / normalStockCount 
      : 0;
    
    // Allocate normal price stocks using floor division
    for (const stock of normalPriceStocks) {
      const shares = Math.floor(newAllocationPerStock / stock.startPrice);
      const investmentUsed = shares * stock.startPrice;
      const unusedAmount = newAllocationPerStock - investmentUsed;
      
      stockAllocations.push({
        symbol: stock.symbol,
        id: stock.id,
        startPrice: stock.startPrice,
        shares,
        investmentUsed,
        unusedAmount,
        direction: stock.direction,
        predicted_price: stock.predicted_price,
        isHighPrice: false
      });
    }
    
    // Handle invalid stocks (no price)
    for (const stock of invalidStocks) {
      stockAllocations.push({
        symbol: stock.symbol,
        id: stock.id,
        startPrice: 0,
        shares: 0,
        investmentUsed: 0,
        unusedAmount: newAllocationPerStock,
        direction: stock.direction,
        predicted_price: stock.predicted_price,
        isHighPrice: false
      });
    }
    
    // ==================== PHASE 3: LEFTOVER POOL REDISTRIBUTION ====================
    // Collect all unused amounts into leftover pool
    let leftoverPool = stockAllocations.reduce((sum, alloc) => sum + alloc.unusedAmount, 0);
    
    // Also add any remaining investment that wasn't allocated to high-price stocks
    // This can happen if high-price stocks took more than their "fair share"
    const totalAllocatedSoFar = stockAllocations.reduce((sum, alloc) => sum + alloc.investmentUsed, 0);
    const unallocatedFromHighPrice = investmentAmount - totalAllocatedSoFar - leftoverPool;
    if (unallocatedFromHighPrice > 0) {
      leftoverPool += unallocatedFromHighPrice;
    }
    
    // Sort stocks by price (cheapest first) for leftover allocation
    const sortedByPrice = [...stockAllocations].sort((a, b) => a.startPrice - b.startPrice);
    
    // Allocate leftover pool to buy additional shares (cheapest stocks first)
    for (let i = 0; i < sortedByPrice.length && leftoverPool > 0; i++) {
      const stock = sortedByPrice[i];
      if (stock.startPrice <= 0) continue;
      
      // Calculate how many additional shares can be bought
      const additionalShares = Math.floor(leftoverPool / stock.startPrice);
      if (additionalShares > 0) {
        const additionalInvestment = additionalShares * stock.startPrice;
        
        // Update the original allocation
        const originalIndex = stockAllocations.findIndex(a => a.id === stock.id);
        if (originalIndex !== -1) {
          stockAllocations[originalIndex].shares += additionalShares;
          stockAllocations[originalIndex].investmentUsed += additionalInvestment;
          stockAllocations[originalIndex].unusedAmount = 0;
          leftoverPool -= additionalInvestment;
        }
      }
    }
    
    // ==================== PHASE 4: FINAL CALCULATIONS ====================
    const totalInvested = stockAllocations.reduce((sum, alloc) => sum + alloc.investmentUsed, 0);
    const unusedCash = investmentAmount - totalInvested;
    
    return {
      allocations: stockAllocations,
      totalInvested,
      unusedCash,
      leftoverPool: leftoverPool,
      highPriceStockCount: highPriceStocks.length,
      initialAllocationPerStock
    };
  };

  const calculateProjectedGrowth = () => {
    if (!investmentAmount || predictions.length === 0) {
      return { 
        total: 0, perStock: 0, percentage: 0, commission: 0, netGain: 0, 
        bullishCount: 0, bearishCount: 0, newsImpactCount: 0, stockCount: 0,
        actualInvested: 0, unusedCash: 0, allocations: []
      };
    }
    
    // Use smart allocation with whole shares
    const smartAllocation = calculateSmartAllocation();
    const { allocations, totalInvested, unusedCash } = smartAllocation;
    
    let totalGrossGain = 0;
    let bullishCount = 0;
    let bearishCount = 0;
    let newsImpactCount = 0;
    
    allocations.forEach(alloc => {
      const { shares, startPrice, direction, predicted_price } = alloc;
      if (shares <= 0 || startPrice <= 0) return;
      
      // Calculate profit based on direction (for pending predictions):
      // BUY (Bullish): Gross Profit = (Predicted - Start) × Shares
      // SHORT (Bearish): Gross Profit = (Start - Predicted) × Shares
      let gain = 0;
      if (direction === "Bullish") {
        gain = shares * (predicted_price - startPrice);
        bullishCount++;
      } else if (direction === "Bearish") {
        gain = shares * (startPrice - predicted_price);
        bearishCount++;
      }
      totalGrossGain += gain;
    });
    
    // Commission is calculated on the ACTUAL invested value (not entered amount)
    const commission = (commissionPct / 100) * totalInvested;
    const netGain = totalGrossGain - commission;
    
    return {
      total: totalGrossGain,
      perStock: totalInvested / predictions.length, // Average per stock
      percentage: totalInvested > 0 ? (totalGrossGain / totalInvested * 100) : 0,
      commission: commission,
      netGain: netGain,
      netPercentage: totalInvested > 0 ? (netGain / totalInvested * 100) : 0,
      bullishCount: bullishCount,
      bearishCount: bearishCount,
      newsImpactCount: newsImpactCount,
      stockCount: predictions.length,
      // New fields for smart allocation
      actualInvested: totalInvested,
      unusedCash: unusedCash,
      allocations: allocations,
      highPriceStockCount: smartAllocation.highPriceStockCount || 0
    };
  };

  const growth = calculateProjectedGrowth();

  // Download predictions as CSV
  const downloadCSV = () => {
    if (predictions.length === 0) {
      toast.error("No data to download");
      return;
    }

    const headers = [
      "Stock",
      "Sector",
      "Start Price",
      "Predicted Price",
      "Change %",
      "Investing Amount",
      "Shares",
      "Strategy",
      "Direction",
      "Expected Date",
      "Projected Profit",
      "Prediction Date"
    ];

    const rows = predictions.map(pred => {
      const startPrice = pred.start_price || pred.current_price;
      const amountPerStock = investmentAmount / predictions.length;
      const shares = amountPerStock / startPrice;
      const changePct = ((pred.predicted_price - startPrice) / startPrice * 100).toFixed(2);
      const strategy = pred.direction === "Bullish" ? "BUY" : pred.direction === "Bearish" ? "SHORT" : "HOLD";
      const priceDiff = Math.abs(pred.predicted_price - startPrice);
      const projectedProfit = (shares * priceDiff).toFixed(2);
      const displaySymbol = pred.symbol.replace(/\.(NS|KA|NP|CM|DU)$/, "");
      
      return [
        displaySymbol,
        pred.sector || "",
        startPrice.toFixed(2),
        pred.predicted_price.toFixed(2),
        changePct,
        amountPerStock.toFixed(0),
        shares.toFixed(2),
        strategy,
        pred.direction,
        getFutureDate(pred.prediction_date),
        projectedProfit,
        pred.prediction_date
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `stocksense_predictions_${currentCountry.id}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success("Downloaded predictions CSV");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold flex items-center gap-2">
              <TrendingUp className="h-8 w-8" />
              StockSense
            </h1>
            <p className="text-gray-400">Quantitative Analysis • 24/7 Monitoring</p>
            <p className="text-xs text-gray-500 mt-1">300+ algorithms analyzing 5+ years of data</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Country Selector */}
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-gray-400" />
              <Select value={selectedCountry} onValueChange={handleCountryChange}>
                <SelectTrigger className="w-[180px]" data-testid="country-selector">
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      {currentCountry.flag} {currentCountry.name}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.id} value={country.id}>
                      <span className="flex items-center gap-2">
                        {country.flag} {country.name} ({country.exchange})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Link to={`/history?country=${selectedCountry}`}>
              <Button variant="outline" size="sm">
                <HistoryIcon className="h-4 w-4 mr-2" />
                History
              </Button>
            </Link>
            <Link to="/batch-jobs">
              <Button variant="outline" data-testid="batch-jobs-btn">
                <Clock className="h-4 w-4 mr-2" />
                Batch Jobs
              </Button>
            </Link>
            <Button onClick={() => fetchPredictions()} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={downloadCSV} disabled={predictions.length === 0} variant="outline" data-testid="download-btn">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            {/* Add Custom Stock Button */}
            <Dialog open={addStockDialogOpen} onOpenChange={(open) => !addingStock && setAddStockDialogOpen(open)}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-purple-600 text-purple-400 hover:bg-purple-900/30" data-testid="add-stock-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stock
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] bg-gray-900 border-gray-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Add Custom Stock</DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Enter a stock symbol to add it to your tracking list. The stock name will be fetched automatically and predictions will be generated instantly.
                  </DialogDescription>
                </DialogHeader>
                {addingStock ? (
                  <div className="py-8 flex flex-col items-center gap-4">
                    <Loader2 className="h-12 w-12 animate-spin text-purple-500" />
                    <div className="text-center">
                      <p className="text-white font-medium">Adding {newStockSymbol}...</p>
                      <p className="text-gray-400 text-sm mt-1">Fetching stock data and generating prediction</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <Label htmlFor="stock-symbol" className="text-white">Stock Symbol *</Label>
                        <Input
                          id="stock-symbol"
                          placeholder="e.g., TATAMOTORS, BAJFINANCE, MARUTI"
                          value={newStockSymbol}
                          onChange={(e) => setNewStockSymbol(e.target.value.toUpperCase())}
                          className="bg-gray-800 border-gray-600"
                          data-testid="add-stock-symbol-input"
                          onKeyDown={(e) => e.key === 'Enter' && handleAddStock()}
                        />
                        <p className="text-xs text-gray-500">
                          Enter NSE stock symbol (e.g., TATAMOTORS). The .NS suffix will be added automatically.
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddStockDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleAddStock} 
                        disabled={!newStockSymbol.trim()}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="add-stock-submit-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Stock
                      </Button>
                    </DialogFooter>
                  </>
                )}
              </DialogContent>
            </Dialog>
            {/* Generate button - always visible for manual trigger */}
            <Button 
              onClick={generatePredictions} 
              disabled={loading} 
              variant="default" 
              className="bg-green-600 hover:bg-green-700"
              data-testid="generate-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {loading ? "Generating..." : "Generate All 20"}
            </Button>
            {/* Admin Sync Button - Only for admin */}
            {user?.username === "admin" && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAdminSync}
                disabled={isSyncing}
                className="border-orange-600 text-orange-400 hover:bg-orange-900/30"
                data-testid="admin-sync-btn"
              >
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                {isSyncing ? "Syncing..." : "Admin Sync"}
              </Button>
            )}
            {/* User Info & Logout */}
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-700">
              <Badge variant="outline" className="text-gray-300">
                <User className="h-3 w-3 mr-1" />
                {user?.name || user?.username}
              </Badge>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Country Info Badge */}
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-900/50 border-blue-700">
            {currentCountry.flag} {currentCountry.exchange} • {currentCountry.currency} {currentCountry.name}
          </Badge>
          {/* WebSocket Connection Status */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Badge 
                  className={`${isConnected ? 'bg-green-900/50 border-green-600' : 'bg-red-900/50 border-red-600'}`}
                  data-testid="websocket-status"
                >
                  {isConnected ? (
                    <>
                      <Wifi className="h-3 w-3 mr-1" />
                      Live
                    </>
                  ) : (
                    <>
                      <WifiOff className="h-3 w-3 mr-1" />
                      Offline
                    </>
                  )}
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">
                  {isConnected 
                    ? "Real-time updates active - prices update automatically" 
                    : "WebSocket disconnected - using manual refresh"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Custom Stocks Section */}
        {customStocks.length > 0 && (
          <Card className="p-4 bg-purple-900/20 border-purple-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-md font-semibold flex items-center gap-2">
                <Plus className="h-4 w-4 text-purple-400" />
                Custom Stocks ({customStocks.length})
              </h3>
              <p className="text-xs text-gray-400">Predictions auto-generated by batch job at 7:00 AM IST</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {customStocks.map((stock) => (
                <div 
                  key={stock.id} 
                  className="flex items-center gap-2 bg-gray-800/50 rounded-lg px-3 py-2 border border-gray-700"
                  data-testid={`custom-stock-${stock.symbol}`}
                >
                  <div className="flex flex-col">
                    <span className="font-semibold text-purple-300">{stock.symbol.replace('.NS', '')}</span>
                    <span className="text-xs text-gray-500">{stock.name}</span>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={stock.status === 'prediction_generated' ? 'border-green-600 text-green-400' : 'border-yellow-600 text-yellow-400'}
                  >
                    {stock.status === 'prediction_generated' ? '✓ Active' : '⏳ Pending'}
                  </Badge>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                          onClick={() => handleGenerateCustomPrediction(stock.id, stock.symbol)}
                          disabled={generatingPrediction === stock.id}
                          data-testid={`generate-prediction-${stock.symbol}`}
                        >
                          {generatingPrediction === stock.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Zap className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Generate prediction now</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                          onClick={() => handleRemoveCustomStock(stock.id, stock.symbol)}
                          data-testid={`remove-stock-${stock.symbol}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Remove from tracking</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Investment Calculator */}
        <Card className="p-6 bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-700">
          <h3 className="text-lg font-semibold mb-2">📊 Investment Calculator - Projected Returns (Whole Shares Only)</h3>
          <p className="text-xs text-gray-400 mb-4">
            Strategy: <span className="text-green-400">BUY</span> Bullish ({growth.bullishCount || 0}) • <span className="text-red-400">SHORT</span> Bearish ({growth.bearishCount || 0})
            • <span className="text-blue-400">{growth.stockCount || 0} stocks</span>
            {growth.highPriceStockCount > 0 && (
              <span className="ml-2">• <span className="text-orange-400">⚠️ {growth.highPriceStockCount} high-price</span> (1 share each)</span>
            )}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Investment Input */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Total Investment</label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-400 text-sm">{currentCountry.currency}</span>
                <Input
                  type="text"
                  value={investmentAmount.toLocaleString('en-IN')}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, '');
                    if (!isNaN(value) && value !== '') {
                      setInvestmentAmount(Number(value));
                    } else if (value === '') {
                      setInvestmentAmount(0);
                    }
                  }}
                  className="pl-10 text-lg font-semibold"
                  placeholder="10,00,000"
                  data-testid="investment-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Enter amount to invest</p>
            </div>

            {/* Actual Invested & Unused Cash */}
            <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
              <p className="text-sm text-gray-400 mb-1">Actual Invested</p>
              <div className="text-xl font-bold text-white">
                {currentCountry.currency}{(growth.actualInvested || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </div>
              {growth.unusedCash > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-600">
                  <p className="text-xs text-gray-400">Unused Cash</p>
                  <div className="text-sm font-semibold text-yellow-400">
                    {currentCountry.currency}{(growth.unusedCash || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-gray-500">Cannot buy whole share</p>
                </div>
              )}
            </div>

            {/* Commission */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Commission %</label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.1"
                  value={commissionPct}
                  onChange={(e) => setCommissionPct(Number(e.target.value))}
                  className="text-lg font-semibold"
                  placeholder="0.2"
                  data-testid="commission-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                -{currentCountry.currency}{(growth.commission || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </p>
            </div>

            {/* Gross Profit */}
            <div>
              <p className="text-sm text-gray-400 mb-2">Gross Profit</p>
              <div className={`text-2xl font-bold ${(growth.total || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(growth.total || 0) >= 0 ? '+' : ''}{currentCountry.currency}{(growth.total || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {(growth.percentage || 0) >= 0 ? '+' : ''}{(growth.percentage || 0).toFixed(2)}% return
              </p>
            </div>

            {/* Net Profit */}
            <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-lg p-3 border border-green-700">
              <p className="text-sm text-gray-400 mb-1">Net Profit</p>
              <div className={`text-2xl font-bold ${(growth.netGain || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(growth.netGain || 0) >= 0 ? '+' : ''}{currentCountry.currency}{(growth.netGain || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </div>
              <p className={`text-xs ${(growth.netPercentage || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(growth.netPercentage || 0) >= 0 ? '+' : ''}{(growth.netPercentage || 0).toFixed(2)}% return
              </p>
            </div>
          </div>
        </Card>

        {/* Predictions Table */}
        {predictions.length === 0 ? (
          <Card className="p-8 text-center">
            <TrendingUp className="h-12 w-12 mx-auto text-gray-500 mb-3" />
            <h3 className="text-xl font-semibold mb-2">No Predictions for {currentCountry.name}</h3>
            <p className="text-gray-400 mb-4">
              Click "Generate" to create predictions for {currentCountry.exchange} stocks
            </p>
            <Button onClick={generatePredictions} disabled={loading}>
              Generate Predictions
            </Button>
          </Card>
        ) : (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Today's Predictions - {currentCountry.name} ({currentCountry.exchange})
                <Badge className="bg-orange-900/50 border-orange-600 text-xs ml-2">🔥 Sorted by Hottest Movers</Badge>
              </h2>
              
              {/* Column Selector Dropdown */}
              <Popover open={columnDropdownOpen} onOpenChange={setColumnDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="justify-between"
                    data-testid="dashboard-column-dropdown"
                  >
                    <div className="flex items-center gap-2">
                      <Columns className="h-4 w-4" />
                      <span>Columns ({visibleColumns.length})</span>
                    </div>
                    <ChevronDown className="h-4 w-4 opacity-50 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-2" align="end">
                  <div className="space-y-2">
                    {/* Header with Select All and Reset */}
                    <div className="flex items-center justify-between border-b border-gray-700 pb-2">
                      <div 
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 rounded p-1"
                        onClick={handleSelectAllColumns}
                      >
                        <Checkbox
                          id="select-all-columns-dashboard"
                          checked={visibleColumns.length === ALL_COLUMNS.length}
                          onCheckedChange={handleSelectAllColumns}
                          data-testid="select-all-columns-dashboard"
                        />
                        <label htmlFor="select-all-columns-dashboard" className="text-sm font-medium cursor-pointer">
                          All
                        </label>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleResetColumns}
                        className="text-xs h-7 px-2"
                      >
                        <Settings2 className="h-3 w-3 mr-1" />
                        Reset
                      </Button>
                    </div>
                    
                    {/* Individual Column Options */}
                    <div className="max-h-[300px] overflow-y-auto space-y-1">
                      {ALL_COLUMNS.map((column) => (
                        <div 
                          key={column.id} 
                          className="flex items-center gap-2 p-2 hover:bg-gray-800 rounded cursor-pointer"
                          onClick={() => handleColumnToggle(column.id)}
                        >
                          <Checkbox
                            id={`dashboard-column-${column.id}`}
                            checked={visibleColumns.includes(column.id)}
                            onCheckedChange={() => handleColumnToggle(column.id)}
                            data-testid={`dashboard-column-${column.id}`}
                          />
                          <label 
                            htmlFor={`dashboard-column-${column.id}`} 
                            className="text-sm cursor-pointer flex-1"
                          >
                            {column.label}
                          </label>
                          {column.default && (
                            <Badge variant="outline" className="text-xs opacity-50">
                              default
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="predictions-table">
                <thead>
                  <tr className="border-b-2 border-gray-700">
                    {isColumnVisible("stock") && <th className="text-left p-3 font-semibold">Stock</th>}
                    {isColumnVisible("startPrice") && <th className="text-right p-3 font-semibold">Start Price</th>}
                    {isColumnVisible("livePrice") && isConnected && <th className="text-right p-3 font-semibold">Live Price</th>}
                    {isColumnVisible("predicted") && <th className="text-right p-3 font-semibold">Predicted (2d)</th>}
                    {isColumnVisible("actualPrice") && <th className="text-center p-3 font-semibold">Actual Price</th>}
                    {isColumnVisible("newsImpact") && <th className="text-center p-3 font-semibold">News Impact</th>}
                    {isColumnVisible("investing") && <th className="text-right p-3 font-semibold">Investing</th>}
                    {isColumnVisible("expectedDate") && <th className="text-center p-3 font-semibold">Expected Date</th>}
                    {isColumnVisible("strategy") && <th className="text-center p-3 font-semibold">Strategy</th>}
                    {isColumnVisible("summary") && <th className="text-left p-3 font-semibold">Prediction Summary</th>}
                    {isColumnVisible("grossProfit") && <th className="text-right p-3 font-semibold">Gross Profit</th>}
                    {isColumnVisible("commission") && <th className="text-right p-3 font-semibold">Commission</th>}
                    {isColumnVisible("netProfit") && <th className="text-right p-3 font-semibold">Net Profit</th>}
                    {isColumnVisible("actions") && <th className="text-center p-3 font-semibold">Evaluate</th>}
                  </tr>
                </thead>
                <tbody>
                  {predictions.map((pred, idx) => {
                    // Get allocation from smart allocation calculation (whole shares only)
                    const predId = pred.id || pred.prediction_id;
                    const allocation = growth.allocations?.find(a => a.id === predId) || {};
                    const shares = allocation.shares || 0;
                    const amountPerStock = allocation.investmentUsed || 0;
                    
                    // Use start_price (fixed at prediction time) with fallback to current_price for backward compatibility
                    const startPrice = pred.start_price || pred.current_price;
                    // Get live price from WebSocket updates
                    const livePrice = livePrices[pred.symbol]?.ltp || priceUpdates[pred.symbol]?.ltp;
                    
                    // Calculate target price incorporating news impact
                    let targetPrice = pred.predicted_price;
                    let hasNewsImpact = false;
                    const newsImpact = pred.news_impact;
                    
                    if (newsImpact?.has_news && newsImpact?.price_data) {
                      const priceData = newsImpact.price_data;
                      
                      // Use price target from news if available and reasonable
                      if (priceData.price_targets && priceData.price_targets.length > 0) {
                        const newsTarget = priceData.price_targets[0];
                        if (newsTarget > startPrice * 0.5 && newsTarget < startPrice * 2) {
                          targetPrice = newsTarget;
                          hasNewsImpact = true;
                        }
                      }
                      
                      // Adjust based on percentage changes from news
                      if (!priceData.price_targets?.length && 
                          priceData.percentage_changes && priceData.percentage_changes.length > 0) {
                        const avgPctChange = priceData.percentage_changes.reduce((a, b) => a + b, 0) / priceData.percentage_changes.length;
                        const predPctChange = ((pred.predicted_price - startPrice) / startPrice) * 100;
                        const blendedPct = predPctChange * 0.7 + avgPctChange * 0.3;
                        targetPrice = startPrice * (1 + blendedPct / 100);
                        hasNewsImpact = true;
                      }
                    }
                    
                    // Calculate profit based on direction (for pending predictions):
                    // BUY: Gross Profit = (Predicted - Start) × Shares
                    // SHORT: Gross Profit = (Start - Predicted) × Shares
                    // Use ORIGINAL predicted_price, NOT the news-adjusted targetPrice
                    let profit = 0;
                    const predictedPrice = pred.predicted_price;
                    if (pred.direction === "Bullish") {
                      // BUY: profit = (predicted - start) * shares
                      profit = shares * (predictedPrice - startPrice);
                    } else if (pred.direction === "Bearish") {
                      // SHORT: profit = (start - predicted) * shares
                      profit = shares * (startPrice - predictedPrice);
                    }
                    const profitPct = amountPerStock > 0 ? (profit / amountPerStock) * 100 : 0;
                    const strategy = pred.direction === "Bullish" ? "BUY" : (pred.direction === "Bearish" ? "SHORT" : "HOLD");
                    
                    // Mark top 3 as "Hot"
                    const isHot = idx < 3;
                    
                    // Check for risk warnings
                    const hasWarnings = pred.risk_warnings && pred.risk_warnings.length > 0;
                    const riskScore = pred.risk_score || 0;
                    const isHighRisk = riskScore >= 50;

                    // Remove exchange suffix from symbol for display
                    const displaySymbol = pred.symbol.replace(/\.(NS|KA|NP|CM|DU)$/, "");

                    return (
                      <tr key={idx} className={`border-b border-gray-800 hover:bg-gray-800/50 ${isHot ? 'bg-orange-900/10' : ''} ${isHighRisk ? 'opacity-70' : ''}`}>
                        {isColumnVisible("stock") && (
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{displaySymbol}</span>
                            {isHot && <span className="text-orange-400 text-xs">🔥</span>}
                            {/* Show institutional score for India */}
                            {selectedCountry === 'india' && pred.institutional_score && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                                      pred.institutional_score >= 70 ? 'bg-green-900/50 text-green-400' :
                                      pred.institutional_score >= 55 ? 'bg-green-900/30 text-green-300' :
                                      pred.institutional_score <= 30 ? 'bg-red-900/50 text-red-400' :
                                      pred.institutional_score <= 45 ? 'bg-red-900/30 text-red-300' :
                                      'bg-gray-700/50 text-gray-300'
                                    }`}>
                                      {pred.institutional_score.toFixed(0)}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-sm bg-gray-900 border-gray-700">
                                    <div className="space-y-2">
                                      <p className="font-bold text-blue-400">📊 Institutional Score: {pred.institutional_score.toFixed(1)}</p>
                                      <p className="text-xs text-gray-300">{pred.direction_strength || pred.direction}</p>
                                      {pred.institutional_analysis && (
                                        <div className="space-y-1 text-xs border-t border-gray-700 pt-2">
                                          <div className="flex justify-between">
                                            <span className="text-gray-400">Technical:</span>
                                            <span className={pred.institutional_analysis.technical?.score >= 60 ? 'text-green-400' : pred.institutional_analysis.technical?.score <= 40 ? 'text-red-400' : 'text-gray-300'}>
                                              {pred.institutional_analysis.technical?.score?.toFixed(0) || '-'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-400">Volume:</span>
                                            <span className={pred.institutional_analysis.volume?.score >= 60 ? 'text-green-400' : pred.institutional_analysis.volume?.score <= 40 ? 'text-red-400' : 'text-gray-300'}>
                                              {pred.institutional_analysis.volume?.score?.toFixed(0) || '-'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-400">RS vs NIFTY:</span>
                                            <span className={pred.institutional_analysis.relative_strength?.score >= 60 ? 'text-green-400' : pred.institutional_analysis.relative_strength?.score <= 40 ? 'text-red-400' : 'text-gray-300'}>
                                              {pred.institutional_analysis.relative_strength?.score?.toFixed(0) || '-'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-400">News:</span>
                                            <span className={pred.institutional_analysis.news_sentiment?.score >= 60 ? 'text-green-400' : pred.institutional_analysis.news_sentiment?.score <= 40 ? 'text-red-400' : 'text-gray-300'}>
                                              {pred.institutional_analysis.news_sentiment?.score?.toFixed(0) || '-'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-400">FII/DII:</span>
                                            <span className={pred.institutional_analysis.fii_dii?.score >= 60 ? 'text-green-400' : pred.institutional_analysis.fii_dii?.score <= 40 ? 'text-red-400' : 'text-gray-300'}>
                                              {pred.institutional_analysis.fii_dii?.score?.toFixed(0) || '-'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-400">Options:</span>
                                            <span className={pred.institutional_analysis.options?.score >= 60 ? 'text-green-400' : pred.institutional_analysis.options?.score <= 40 ? 'text-red-400' : 'text-gray-300'}>
                                              {pred.institutional_analysis.options?.score?.toFixed(0) || '-'}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      {pred.top_drivers && pred.top_drivers.length > 0 && (
                                        <div className="border-t border-gray-700 pt-2">
                                          <p className="text-xs text-gray-400 font-semibold">Top Drivers:</p>
                                          {pred.top_drivers.slice(0, 3).map((driver, i) => (
                                            <p key={i} className="text-xs text-gray-300">• {driver}</p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {hasWarnings && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <AlertTriangle className={`h-4 w-4 ${isHighRisk ? 'text-red-400' : 'text-yellow-400'}`} />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs bg-gray-900 border-gray-700">
                                    <div className="space-y-2">
                                      <p className="font-bold text-red-400">⚠️ Risk Warnings (Score: {riskScore})</p>
                                      {pred.risk_warnings.map((w, i) => (
                                        <div key={i} className="text-xs">
                                          <p className={`font-semibold ${w.severity === 'high' ? 'text-red-400' : w.severity === 'medium' ? 'text-yellow-400' : 'text-gray-400'}`}>
                                            {w.message}
                                          </p>
                                          <p className="text-gray-400">{w.suggestion}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </td>
                        )}
                        {isColumnVisible("startPrice") && (
                        <td className="p-3 text-right">
                          <div className="font-semibold">{currentCountry.currency}{Number(startPrice).toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{formatTimestamp(pred.start_price_timestamp || pred.prediction_date)}</div>
                        </td>
                        )}
                        {isColumnVisible("livePrice") && isConnected && (
                          <td className="p-3 text-right">
                            {livePrice ? (
                              <div>
                                <div className={`font-semibold ${
                                  livePrice > startPrice ? 'text-green-400' : 
                                  livePrice < startPrice ? 'text-red-400' : 'text-gray-300'
                                }`}>
                                  {currentCountry.currency}{Number(livePrice).toFixed(2)}
                                </div>
                                <div className={`text-xs ${
                                  livePrice > startPrice ? 'text-green-500' : 
                                  livePrice < startPrice ? 'text-red-500' : 'text-gray-500'
                                }`}>
                                  {livePrice > startPrice ? '▲' : livePrice < startPrice ? '▼' : '—'} 
                                  {((livePrice - startPrice) / startPrice * 100).toFixed(2)}%
                                </div>
                              </div>
                            ) : (
                              <div className="text-gray-500 text-sm">—</div>
                            )}
                          </td>
                        )}
                        {isColumnVisible("predicted") && (
                        <td className="p-3 text-right">
                          <div className={`font-bold ${pred.direction === "Bullish" ? 'text-green-400' : pred.direction === "Bearish" ? 'text-red-400' : 'text-gray-400'}`}>
                            {currentCountry.currency}{Number(pred.predicted_price).toFixed(2)}
                          </div>
                        </td>
                        )}
                        {isColumnVisible("actualPrice") && (
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Enter price"
                              value={actualPriceInputs[pred.prediction_id] || ''}
                              onChange={(e) => handleActualPriceChange(pred.prediction_id, e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleManualEvaluate(pred)}
                              className="w-24 h-8 text-sm bg-gray-800 border-gray-600 text-right"
                              data-testid={`actual-price-input-${pred.symbol}`}
                              disabled={evaluatingStock === pred.prediction_id}
                            />
                          </div>
                        </td>
                        )}
                        {isColumnVisible("newsImpact") && (
                        <td className="p-3 text-center" data-testid={`news-impact-${pred.symbol}`}>
                          {hasNewsImpact ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <div className="cursor-pointer">
                                    <div className={`font-bold ${
                                      targetPrice > pred.predicted_price ? 'text-green-400' :
                                      targetPrice < pred.predicted_price ? 'text-red-400' :
                                      'text-blue-400'
                                    }`}>
                                      {currentCountry.currency}{targetPrice.toFixed(2)}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      {pred.news_impact?.news_timestamp ? (
                                        new Date(pred.news_impact.news_timestamp).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })
                                      ) : 'Recent'}
                                    </div>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-sm bg-gray-900 border-gray-700">
                                  <div className="space-y-2">
                                    <p className="font-bold text-blue-400">📰 News-Adjusted Price</p>
                                    <div className="text-xs">
                                      <p className="text-gray-400">Original Prediction: <span className="text-white">{currentCountry.currency}{pred.predicted_price.toFixed(2)}</span></p>
                                      <p className="text-gray-400">News-Adjusted: <span className="text-green-400">{currentCountry.currency}{targetPrice.toFixed(2)}</span></p>
                                      <p className="text-gray-400">Difference: <span className={targetPrice > pred.predicted_price ? 'text-green-400' : 'text-red-400'}>
                                        {targetPrice > pred.predicted_price ? '+' : ''}{((targetPrice - pred.predicted_price) / pred.predicted_price * 100).toFixed(2)}%
                                      </span></p>
                                    </div>
                                    {pred.news_impact?.price_data?.price_targets?.length > 0 && (
                                      <div className="text-xs text-green-400 border-t border-gray-700 pt-2">
                                        News Target: {currentCountry.currency}{pred.news_impact.price_data.price_targets[0].toFixed(2)}
                                      </div>
                                    )}
                                    {pred.news_impact?.price_data?.percentage_changes?.length > 0 && (
                                      <div className="text-xs border-t border-gray-700 pt-2">
                                        News % Changes: {pred.news_impact.price_data.percentage_changes.map((pct, i) => (
                                          <span key={i} className={pct > 0 ? 'text-green-400' : 'text-red-400'}>
                                            {pct > 0 ? '+' : ''}{pct.toFixed(1)}%{i < pred.news_impact.price_data.percentage_changes.length - 1 ? ', ' : ''}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {pred.news_impact?.latest_news && (
                                      <div className="text-xs border-t border-gray-700 pt-2">
                                        <p className="text-gray-300">{pred.news_impact.latest_news.title}</p>
                                      </div>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <div className="text-xs text-gray-500">No News</div>
                          )}
                        </td>
                        )}
                        {isColumnVisible("investing") && (
                        <td className="p-3 text-right">
                          <div className="font-semibold text-blue-300">
                            {currentCountry.currency}{amountPerStock.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          </div>
                          <div className="text-xs text-gray-500">
                            {shares} shares
                          </div>
                        </td>
                        )}
                        {isColumnVisible("expectedDate") && (
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Calendar className="h-3 w-3 text-blue-400" />
                            <span className="text-sm text-blue-400">{getFutureDate(pred.prediction_date)}</span>
                          </div>
                        </td>
                        )}
                        {isColumnVisible("strategy") && (
                        <td className="p-3 text-center">
                          <Badge
                            className={
                              strategy === "BUY" ? "bg-green-900/50 border-green-700" :
                              strategy === "SHORT" ? "bg-orange-900/50 border-orange-700" :
                              "bg-gray-800 border-gray-700"
                            }
                          >
                            {strategy === "BUY" ? "📈 BUY" : strategy === "SHORT" ? "📉 SHORT" : "⏸️ HOLD"}
                          </Badge>
                        </td>
                        )}
                        {isColumnVisible("summary") && (
                        <td className="p-3 text-left">
                          {/* Prediction Summary Column */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="cursor-pointer max-w-[200px]">
                                  {/* Rating Badges */}
                                  <div className="flex flex-wrap gap-1 mb-1">
                                    {pred.institutional_analysis?.technical && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        pred.institutional_analysis.technical.score >= 60 ? 'bg-green-900/50 text-green-400' :
                                        pred.institutional_analysis.technical.score <= 40 ? 'bg-red-900/50 text-red-400' :
                                        'bg-gray-700/50 text-gray-300'
                                      }`}>
                                        T:{pred.institutional_analysis.technical.score?.toFixed(0) || '-'}
                                      </span>
                                    )}
                                    {pred.institutional_analysis?.volume && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        pred.institutional_analysis.volume.score >= 60 ? 'bg-green-900/50 text-green-400' :
                                        pred.institutional_analysis.volume.score <= 40 ? 'bg-red-900/50 text-red-400' :
                                        'bg-gray-700/50 text-gray-300'
                                      }`}>
                                        V:{pred.institutional_analysis.volume.score?.toFixed(0) || '-'}
                                      </span>
                                    )}
                                    {pred.institutional_analysis?.news_sentiment && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        pred.institutional_analysis.news_sentiment.score >= 60 ? 'bg-green-900/50 text-green-400' :
                                        pred.institutional_analysis.news_sentiment.score <= 40 ? 'bg-red-900/50 text-red-400' :
                                        'bg-gray-700/50 text-gray-300'
                                      }`}>
                                        N:{pred.institutional_analysis.news_sentiment.score?.toFixed(0) || '-'}
                                      </span>
                                    )}
                                  </div>
                                  {/* Summary Text */}
                                  <div className="text-xs text-gray-400 truncate">
                                    {pred.institutional_analysis?.technical?.moving_averages?.macd_signal === 'bullish' ? '📈 MACD Bullish' :
                                     pred.institutional_analysis?.technical?.moving_averages?.macd_signal === 'bearish' ? '📉 MACD Bearish' : ''}
                                    {pred.institutional_analysis?.technical?.rsi_signal === 'oversold' ? ' | RSI Oversold' :
                                     pred.institutional_analysis?.technical?.rsi_signal === 'overbought' ? ' | RSI Overbought' : ''}
                                  </div>
                                  {pred.confidence && (
                                    <div className="text-xs text-blue-400">
                                      Confidence: {pred.confidence}%
                                    </div>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-md bg-gray-900 border-gray-700 p-3">
                                <div className="space-y-3">
                                  <p className="font-bold text-blue-400 border-b border-gray-700 pb-2">📊 Prediction Analysis</p>
                                  
                                  {/* Overall Score */}
                                  {pred.institutional_score && (
                                    <div className="flex justify-between items-center">
                                      <span className="text-gray-400">Overall Score:</span>
                                      <span className={`font-bold ${
                                        pred.institutional_score >= 60 ? 'text-green-400' :
                                        pred.institutional_score <= 40 ? 'text-red-400' : 'text-yellow-400'
                                      }`}>{pred.institutional_score.toFixed(1)}/100</span>
                                    </div>
                                  )}
                                  
                                  {/* Technical Analysis */}
                                  {pred.institutional_analysis?.technical && (
                                    <div className="border-t border-gray-700 pt-2">
                                      <p className="text-sm font-semibold text-purple-400 mb-1">📈 Technical ({pred.institutional_analysis.technical.score?.toFixed(0)})</p>
                                      <div className="text-xs space-y-1 text-gray-300">
                                        <div>RSI: {pred.institutional_analysis.technical.rsi?.toFixed(1)} ({pred.institutional_analysis.technical.rsi_signal})</div>
                                        <div>MACD: {pred.institutional_analysis.technical.moving_averages?.macd_signal}</div>
                                        <div>Momentum: {pred.institutional_analysis.technical.momentum?.momentum_pct?.toFixed(2)}%</div>
                                        {pred.institutional_analysis.technical.moving_averages?.death_cross && <div className="text-red-400">⚠️ Death Cross</div>}
                                        {pred.institutional_analysis.technical.moving_averages?.golden_cross && <div className="text-green-400">✨ Golden Cross</div>}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Volume Analysis */}
                                  {pred.institutional_analysis?.volume && (
                                    <div className="border-t border-gray-700 pt-2">
                                      <p className="text-sm font-semibold text-cyan-400 mb-1">📊 Volume ({pred.institutional_analysis.volume.score?.toFixed(0)})</p>
                                      <div className="text-xs space-y-1 text-gray-300">
                                        <div>Volume Ratio: {pred.institutional_analysis.volume.breakout?.volume_ratio?.toFixed(2)}x</div>
                                        <div>Delivery: {pred.institutional_analysis.volume.delivery?.delivery_pct?.toFixed(1)}% ({pred.institutional_analysis.volume.delivery?.signal})</div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* News Sentiment */}
                                  {pred.institutional_analysis?.news_sentiment && (
                                    <div className="border-t border-gray-700 pt-2">
                                      <p className="text-sm font-semibold text-yellow-400 mb-1">📰 News ({pred.institutional_analysis.news_sentiment.score?.toFixed(0)})</p>
                                      <div className="text-xs space-y-1 text-gray-300">
                                        <div>Sentiment: {pred.institutional_analysis.news_sentiment.sentiment}</div>
                                        <div>News Count: {pred.institutional_analysis.news_sentiment.news_count} (📈{pred.institutional_analysis.news_sentiment.bullish_count} / 📉{pred.institutional_analysis.news_sentiment.bearish_count})</div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Relative Strength */}
                                  {pred.institutional_analysis?.relative_strength && (
                                    <div className="border-t border-gray-700 pt-2">
                                      <p className="text-sm font-semibold text-orange-400 mb-1">💪 RS vs Index ({pred.institutional_analysis.relative_strength.score?.toFixed(0)})</p>
                                      <div className="text-xs space-y-1 text-gray-300">
                                        <div>Stock Return: {pred.institutional_analysis.relative_strength.stock_return?.toFixed(2)}%</div>
                                        <div>Index Return: {pred.institutional_analysis.relative_strength.nifty_return?.toFixed(2)}%</div>
                                        <div className={pred.institutional_analysis.relative_strength.outperforming ? 'text-green-400' : 'text-red-400'}>
                                          {pred.institutional_analysis.relative_strength.outperforming ? '✅ Outperforming' : '❌ Underperforming'}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Expected Return */}
                                  {pred.expected_return_range && (
                                    <div className="border-t border-gray-700 pt-2">
                                      <p className="text-sm font-semibold text-green-400 mb-1">🎯 Expected Return</p>
                                      <div className="text-xs text-gray-300">
                                        Range: {pred.expected_return_range.min?.toFixed(2)}% to {pred.expected_return_range.max?.toFixed(2)}%
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                        )}
                        {isColumnVisible("grossProfit") && (
                        <td className="p-3 text-right">
                          {/* Gross Profit Column */}
                          {(() => {
                            const isPositive = profit >= 0;
                            return (
                              <div className={`font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                                {isPositive ? '+' : ''}{currentCountry.currency}{profit.toFixed(2)}
                                {hasNewsImpact && <span className="text-blue-400 text-xs ml-1">📰</span>}
                              </div>
                            );
                          })()}
                        </td>
                        )}
                        {isColumnVisible("commission") && (
                        <td className="p-3 text-right">
                          {/* Commission Column */}
                          {(() => {
                            const commissionPerStock = (commissionPct / 100) * amountPerStock;
                            return (
                              <div className="text-red-400 text-sm">
                                -{currentCountry.currency}{commissionPerStock.toFixed(2)}
                              </div>
                            );
                          })()}
                        </td>
                        )}
                        {isColumnVisible("netProfit") && (
                        <td className="p-3 text-right">
                          {/* Net Profit Column */}
                          {(() => {
                            const commissionPerStock = (commissionPct / 100) * amountPerStock;
                            const netProfit = profit - commissionPerStock;
                            const isPositive = netProfit >= 0;
                            return (
                              <div className={`font-bold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                                {isPositive ? '+' : ''}{currentCountry.currency}{netProfit.toFixed(2)}
                              </div>
                            );
                          })()}
                        </td>
                        )}
                        {isColumnVisible("actions") && (
                        <td className="p-3 text-center">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className={`h-8 px-3 ${
                                    actualPriceInputs[pred.prediction_id] 
                                      ? 'border-green-600 text-green-400 hover:bg-green-900/30' 
                                      : 'border-gray-600 text-gray-400'
                                  }`}
                                  onClick={() => handleManualEvaluate(pred)}
                                  disabled={!actualPriceInputs[pred.prediction_id] || evaluatingStock === pred.prediction_id}
                                  data-testid={`evaluate-btn-${pred.symbol}`}
                                >
                                  {evaluatingStock === pred.prediction_id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">
                                  {actualPriceInputs[pred.prediction_id] 
                                    ? `Evaluate ${pred.symbol.replace('.NS', '')} @ ₹${actualPriceInputs[pred.prediction_id]}`
                                    : 'Enter actual price to evaluate'
                                  }
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
